<template>
    <span class="human-date">{{ getDateString() }}</span>
</template>
<script>
export default {
    name: "human-date",
    props: {
        date: String,
        format: {
            type: String,
            default: null,
        },
        // When set to an IANA timezone (e.g. the place timezone), the instant
        // is rendered on that timezone's wall clock regardless of any offset
        // embedded in the string or the browser timezone. Leave null to keep
        // the legacy offset-aware / browser-local behaviour.
        timezone: {
            type: String,
            default: null,
        },
    },
    computed: {
        dateObject: function () {
            if (this.date) {
                let date = new Date(this.date);
                if (!isNaN(date.getMonth())) {
                    return date;
                }
                return new Date(this.date.replace(/-/g, "/"));
            }
            return new Date();
        },
        // Timezone offset (in minutes) explicitly carried by the date string,
        // or null when the string is naive. RFC2822 ("... -0700"), ISO with
        // "Z" or "+07:00"/"-0700" are all supported. Only offset-bearing
        // strings represent an absolute instant that must be rendered at that
        // offset rather than in the browser's timezone. Naive strings
        // ("YYYY-MM-DD[ HH:mm:ss]", e.g. birthdays) keep legacy local parsing
        // to avoid day shifts.
        explicitOffsetMinutes: function () {
            if (!this.date) {
                return null;
            }
            const str = String(this.date);
            if (/(?:Z|\bUTC\b|\bGMT\b)(?![+-])/i.test(str)) {
                return 0;
            }
            const match = str.match(/([+-])(\d{2}):?(\d{2})(?!\d)/);
            if (match) {
                const sign = match[1] === "-" ? -1 : 1;
                return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
            }
            return null;
        },
        // Wall-clock parts. For offset-bearing values we read the instant at
        // its own offset (timezone-independent of browser/store); naive values
        // fall back to the legacy local getters.
        parts: function () {
            const date = this.dateObject;
            // Explicit timezone wins: render the absolute instant on the given
            // timezone's wall clock, independent of the string's own offset and
            // the browser. Used to show place-local times (e.g. campaigns).
            if (
                this.timezone &&
                typeof Intl !== "undefined" &&
                Intl.DateTimeFormat &&
                !isNaN(date.getTime())
            ) {
                const dtf = new Intl.DateTimeFormat("en-US", {
                    timeZone: this.timezone,
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hourCycle: "h23",
                });
                const tzParts = {};
                dtf.formatToParts(date).forEach((p) => {
                    if (p.type !== "literal") {
                        tzParts[p.type] = parseInt(p.value, 10);
                    }
                });
                const monthIndex = tzParts.month - 1;
                return {
                    year: tzParts.year,
                    monthIndex: monthIndex,
                    day: tzParts.day,
                    hour: tzParts.hour % 24,
                    minute: tzParts.minute,
                    second: tzParts.second,
                    weekdayIndex: new Date(
                        Date.UTC(tzParts.year, monthIndex, tzParts.day)
                    ).getUTCDay(),
                };
            }
            const offset = this.explicitOffsetMinutes;
            if (offset === null || isNaN(date.getTime())) {
                return {
                    year: date.getFullYear(),
                    monthIndex: date.getMonth(),
                    day: date.getDate(),
                    hour: date.getHours(),
                    minute: date.getMinutes(),
                    second: date.getSeconds(),
                    weekdayIndex: date.getDay(),
                };
            }
            const shifted = new Date(date.getTime() + offset * 60000);
            return {
                year: shifted.getUTCFullYear(),
                monthIndex: shifted.getUTCMonth(),
                day: shifted.getUTCDate(),
                hour: shifted.getUTCHours(),
                minute: shifted.getUTCMinutes(),
                second: shifted.getUTCSeconds(),
                weekdayIndex: shifted.getUTCDay(),
            };
        },
        month: function () {
            // dates-full_months may be an array or an object keyed "0".."11";
            // both support numeric index access.
            let months = this.t("dates-full_months");
            return months && months[this.parts.monthIndex] != null
                ? months[this.parts.monthIndex]
                : "";
        },
        monthNumber: function () {
            return this.zeroLPad(this.parts.monthIndex + 1);
        },
        weekDay: function () {
            let weekDays = this.t("dates-full_weekdays");
            return weekDays && weekDays[this.parts.weekdayIndex] != null
                ? weekDays[this.parts.weekdayIndex]
                : "";
        },
        hour: function () {
            return this.zeroLPad(this.parts.hour);
        },
        minute: function () {
            return this.zeroLPad(this.parts.minute);
        },
        year: function () {
            return this.parts.year;
        },
        twoDigitYear() {
            return String(this.year).substr(2, 2);
        },
        day: function () {
            return this.zeroLPad(this.parts.day);
        },
        second: function () {
            return this.zeroLPad(this.parts.second);
        },
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        zeroLPad: function (n) {
            if (n < 10) {
                n = "0" + (n + "");
            }
            return n;
        },
        getDateString: function () {
            let replaces = {
                d: this.day,
                h: this.hour,
                m: this.minute,
                s: this.second,
                D: this.weekDay,
                M: this.monthNumber,
                MM: this.month,
                Y: this.twoDigitYear,
                YY: this.year,
            };
            let string = this.format || this.t("dates-default_format");
            for (let r in replaces) {
                let replace = replaces[r] + "";
                let replaceUcFirst =
                    replace.charAt(0).toUpperCase() + replace.slice(1);
                string = string.replace("[" + r + "]:ucf", replaceUcFirst);
                string = string.replace(
                    "[" + r + "]:uc",
                    replace.toUpperCase(),
                );
                string = string.replace("[" + r + "]", replace);
            }
            return string;
        },
    },
};
</script>
